import type { ViewHook } from 'phoenix_live_view';
import { useCallback } from 'react';

import type { JsonObject } from './utils';

type EventHandler = string | (() => void);
type ChangeHandler = string | ((value: string) => void);
type LiveSocketOwnerView = {
  pushEvent?: (event: string, payload: JsonObject) => void;
  pushEventTo?: (target: unknown, event: string, payload: JsonObject) => void;
};
type LiveSocketLike = {
  owner?: (element: HTMLElement, callback: (view: LiveSocketOwnerView) => void) => void;
};
type PushEventTo = NonNullable<ViewHook['pushEventTo']>;
type PushEventToTarget = Parameters<PushEventTo>[0];

type UseLiveViewBridgeParams = {
  target?: string | number | undefined;
  hiddenInputId?: string | undefined;
  fieldName?: string | undefined;
  pushEvent?: ViewHook['pushEvent'] | undefined;
  pushEventTo?: ViewHook['pushEventTo'] | undefined;
};

const normalizeTarget = (value: string | number | null | undefined): string | number | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;

  const target = value.trim();
  if (!target) return undefined;

  return /^\d+$/.test(target) ? Number.parseInt(target, 10) : target;
};

export const useLiveViewBridge = ({
  target,
  hiddenInputId,
  fieldName,
  pushEvent,
  pushEventTo,
}: UseLiveViewBridgeParams) => {
  const getTargetEl = useCallback(() => {
    if (!target || typeof target !== 'string') return null;
    return document.getElementById(target);
  }, [target]);

  const targetFromInput = useCallback(
    (attributeName: string) => {
      const hiddenInput = hiddenInputId ? document.getElementById(hiddenInputId) : null;
      return normalizeTarget(hiddenInput?.getAttribute(attributeName));
    },
    [hiddenInputId],
  );

  const resolveTarget = useCallback(
    (preferredTarget?: string | number): PushEventToTarget | null => {
      const candidate = normalizeTarget(preferredTarget) ?? normalizeTarget(target);
      if (candidate === undefined || candidate === null) return getTargetEl();
      if (typeof candidate === 'number') return candidate;
      return document.getElementById(candidate) ?? candidate;
    },
    [target, getTargetEl],
  );

  const resolveEventTarget = useCallback((): PushEventToTarget | null => {
    const liveTarget = targetFromInput('phx-target');
    if (liveTarget === undefined || liveTarget === null) return null;
    return resolveTarget(liveTarget);
  }, [resolveTarget, targetFromInput]);

  const resolveSuperTextTarget = useCallback((): PushEventToTarget | null => {
    const liveTarget = targetFromInput('data-supertext-target');
    if (liveTarget === undefined || liveTarget === null) return null;
    return resolveTarget(liveTarget);
  }, [resolveTarget, targetFromInput]);

  const pushThroughLiveSocketOwner = useCallback(
    (eventName: string, payload: JsonObject) => {
      const liveSocket = (window as Window & { liveSocket?: LiveSocketLike }).liveSocket;
      if (!liveSocket?.owner) return false;

      const ownerElementCandidates: (HTMLElement | null | undefined)[] = [
        hiddenInputId ? document.getElementById(hiddenInputId) : null,
        typeof target === 'string' ? document.getElementById(target) : null,
        typeof fieldName === 'string' ? (document.getElementsByName(fieldName)[0] as HTMLElement | undefined) : null,
      ];
      const ownerElement = ownerElementCandidates.find(Boolean);
      if (!ownerElement) return false;

      try {
        liveSocket.owner(ownerElement, view => {
          const explicitTarget = resolveEventTarget();
          if (explicitTarget && typeof view?.pushEventTo === 'function') {
            view.pushEventTo(explicitTarget, eventName, payload);
            return;
          }
          if (typeof view?.pushEvent === 'function') {
            view.pushEvent(eventName, payload);
          }
        });
        return true;
      } catch {
        return false;
      }
    },
    [hiddenInputId, target, fieldName, resolveEventTarget],
  );

  const emitEvent = useCallback(
    (handler: EventHandler | undefined, payload: JsonObject) => {
      if (!handler) return;

      if (typeof handler === 'function') {
        handler();
        return;
      }

      const targetParam = resolveEventTarget();
      if (pushEventTo && targetParam) {
        pushEventTo(targetParam, handler, payload);
        return;
      }

      if (pushEvent) {
        pushEvent(handler, payload);
        return;
      }

      pushThroughLiveSocketOwner(handler, payload);
    },
    [resolveEventTarget, pushEventTo, pushEvent, pushThroughLiveSocketOwner],
  );

  const emitChange = useCallback(
    (handler: ChangeHandler | undefined, nextValue: string) => {
      if (!handler) return;

      if (typeof handler === 'function') {
        handler(nextValue);
        return;
      }

      const targetParam = resolveEventTarget();
      if (pushEventTo && targetParam) {
        pushEventTo(targetParam, handler, { value: nextValue });
        return;
      }

      pushEvent?.(handler, { value: nextValue });
    },
    [resolveEventTarget, pushEventTo, pushEvent],
  );

  return {
    resolveEventTarget,
    resolveSuperTextTarget,
    emitEvent,
    emitChange,
  };
};
